import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { completeFederatedProfile, logoutUser } from "../authService";
import { useLanguage } from "../i18n/LanguageProvider";
import {
  VAULTQUEST_AGE_GROUPS,
  isAdultAgeGroup,
} from "../utils/aiConflictConsent";

const SELECT_ARROW_ICON =
  "url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJtNiA5IDYgNiA2LTYiLz48L3N2Zz4=)";

function mapCompletionError(error, t) {
  const message = String(error?.message || "");
  const code = String(error?.code || "");
  if (message.includes("USERNAME_ALREADY_TAKEN") || code.includes("already-exists")) {
    return t("authProfileCompletion.errors.usernameTaken");
  }
  if (message.includes("INVALID_USERNAME")) return t("register.errors.usernameInvalid");
  if (message.includes("RESERVED_USERNAME")) return t("register.errors.usernameReserved");
  if (message.includes("USERNAME_NOT_ALLOWED")) return t("register.errors.usernameNotAllowed");
  if (message.includes("INVALID_CONSOLE_TYPE")) return t("register.errors.consoleTypeRequired");
  if (message.includes("MINIMUM_AGE_NOT_CONFIRMED")) {
    return t("register.errors.minimumAgeConfirmationRequired");
  }
  if (message.includes("LEGAL_NOT_ACCEPTED")) return t("register.errors.acceptLegalRequired");
  if (code.includes("failed-precondition")) return t("authProfileCompletion.errors.providerRequired");
  return t("authProfileCompletion.errors.generic");
}

export default function ProfileCompletionPage() {
  const { t, language } = useLanguage();
  const [username, setUsername] = useState("");
  const [consoleType, setConsoleType] = useState("");
  const [age, setAge] = useState("");
  const [confirmedMinimumAge, setConfirmedMinimumAge] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [aiConflictAnalysisConsent, setAiConflictAnalysisConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canUseAiConflictAnalysis = isAdultAgeGroup(age);

  useEffect(() => {
    if (!canUseAiConflictAnalysis && aiConflictAnalysisConsent) {
      setAiConflictAnalysisConsent(false);
    }
  }, [canUseAiConflictAnalysis, aiConflictAnalysisConsent]);

  const handleSubmit = async () => {
    setError("");
    const cleanUsername = username.trim();
    if (!/^[a-zA-Z0-9_]{3,15}$/.test(cleanUsername)) {
      setError(t("register.errors.usernameInvalid"));
      return;
    }
    if (!consoleType) {
      setError(t("register.errors.consoleTypeRequired"));
      return;
    }
    if (!confirmedMinimumAge) {
      setError(t("register.errors.minimumAgeConfirmationRequired"));
      return;
    }
    if (!acceptedLegal) {
      setError(t("register.errors.acceptLegalRequired"));
      return;
    }

    setSubmitting(true);
    try {
      await completeFederatedProfile({
        username: cleanUsername,
        consoleType,
        age,
        language,
        confirmedMinimumAge,
        acceptedLegal,
        aiConflictAnalysisConsent: canUseAiConflictAnalysis && aiConflictAnalysisConsent,
      });
    } catch (completionError) {
      setError(mapCompletionError(completionError, t));
      setSubmitting(false);
    }
  };

  const fieldStyle = {
    width: "100%",
    height: 60,
    borderRadius: 18,
    border: "2px solid rgba(255,255,255,0.18)",
    background: "rgba(4,2,39,0.78)",
    color: "white",
    padding: "0 18px",
    boxSizing: "border-box",
    fontSize: 16,
    fontWeight: 900,
    outline: "none",
  };

  const selectFieldStyle = {
    ...fieldStyle,
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage: SELECT_ARROW_ICON,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 18px center",
    backgroundSize: "18px 18px",
    paddingRight: 50,
  };

  return (
    <main className="vq-profile-completion">
      <section className="vq-profile-completion__card">
        <img className="vq-profile-completion__logo" src="/vaultquest-logo.png" alt="VaultQuest" />
        <h1>{t("authProfileCompletion.title")}</h1>
        <p className="vq-profile-completion__intro">{t("authProfileCompletion.subtitle")}</p>

        <div className="vq-profile-completion__fields">
          <input
            style={fieldStyle}
            value={username}
            maxLength={15}
            autoComplete="username"
            placeholder={t("register.usernamePlaceholder")}
            onChange={(event) => setUsername(event.target.value)}
          />
          <select
            className="vq-profile-completion__select"
            style={selectFieldStyle}
            value={consoleType}
            onChange={(event) => setConsoleType(event.target.value)}
          >
            <option value="" disabled>{t("register.consoleTypePlaceholder")}</option>
            <option value="PLAYSTATION">{t("register.consoleOptions.playstation")}</option>
            <option value="XBOX">{t("register.consoleOptions.xbox")}</option>
            <option value="PC">{t("register.consoleOptions.pc")}</option>
          </select>
          <select
            className="vq-profile-completion__select"
            style={selectFieldStyle}
            value={age}
            onChange={(event) => {
              const nextAge = event.target.value;
              setAge(nextAge);
              if (!isAdultAgeGroup(nextAge)) {
                setAiConflictAnalysisConsent(false);
              }
            }}
          >
            <option value="">{t("register.agePlaceholderOptional")}</option>
            {VAULTQUEST_AGE_GROUPS.map((group) => <option key={group} value={group}>{group}</option>)}
          </select>
        </div>

        <label className="vq-profile-completion__check">
          <input
            type="checkbox"
            checked={confirmedMinimumAge}
            onChange={(event) => setConfirmedMinimumAge(event.target.checked)}
          />
          <span>{t("register.minimumAgeConfirmation")} *</span>
        </label>

        <label className="vq-profile-completion__check">
          <input
            type="checkbox"
            checked={acceptedLegal}
            onChange={(event) => setAcceptedLegal(event.target.checked)}
          />
          <span>
            {t("register.acceptPrefix")} <Link to="/agb">{t("register.terms")}</Link>{" "}
            {t("register.acceptMiddle")} <Link to="/datenschutz">{t("register.privacy")}</Link>{" "}
            {t("register.acceptSuffix")}
            {" *"}
          </span>
        </label>

        <div className="vq-profile-completion__required-hint">
          {t("register.requiredFieldHint")}
        </div>

        <label className="vq-profile-completion__check">
          <input
            type="checkbox"
            checked={canUseAiConflictAnalysis && aiConflictAnalysisConsent}
            disabled={!canUseAiConflictAnalysis}
            onChange={(event) => {
              if (!canUseAiConflictAnalysis) {
                setAiConflictAnalysisConsent(false);
                return;
              }
              setAiConflictAnalysisConsent(event.target.checked);
            }}
          />
          <span>
            <strong style={{ display: "block", marginBottom: 3 }}>
              {t("register.aiConflictConsentTitle")}
            </strong>
            {t("register.aiConflictConsentTextBeforePrivacy")}{" "}
            <Link to="/datenschutz">{t("register.privacy")}</Link>
            {t("register.aiConflictConsentTextAfterPrivacy")}
            {!canUseAiConflictAnalysis && (
              <span className="vq-profile-completion__consent-hint">
                {t("register.aiConflictConsentAdultOnly")}
              </span>
            )}
          </span>
        </label>

        {error && <div className="vq-profile-completion__error" role="alert">{error}</div>}

        <button
          type="button"
          className="vq-profile-completion__submit"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? t("authProfileCompletion.saving") : t("authProfileCompletion.submit")}
        </button>
        <button
          type="button"
          className="vq-profile-completion__cancel"
          disabled={submitting}
          onClick={() => logoutUser()}
        >
          {t("common.cancel")}
        </button>
      </section>
    </main>
  );
}
